import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import {
  getFacilityHierarchyId,
  resolveFacilityRegistryDisplayIds,
  bareNpiFromCcn,
} from "@utils/facilityUtils";
import type { FacilityNetworkViewModel } from "@utils/facilityNetworkView";
import type { ChildLinkRequestItem, LinkedFacilityApiRow } from "@services/facilityNetworkLink";
import type { MyFacilityListRow } from "types/myFacilityListRow";

/** @deprecated Pending/declined/invite also appear on parent Linked Facilities roster. */
export type ChildFacilityStatus = "linked" | "pending" | "declined" | "cancelled" | "invite";

export type RosterFacilityStatus = "active" | "recently_linked";

export type LinkedFacilitySortKey = "facility" | "type" | "location" | "status" | "penalty";

export type LinkedFacilityRow = {
  key: string;
  facility: FacilityHierarchyInput | null;
  facilityId: number | null;
  name: string;
  /** Muted sub-text under name — labeled CCN (AB#10988). */
  ccnLabel: string;
  npiLabel: string;
  npiDigits: string;
  ccnDigits: string;
  typeLabel: string;
  location: string;
  status: ChildFacilityStatus;
  rosterStatus: RosterFacilityStatus;
  linkedAt: string | null;
  penaltyExposure: number;
};

export const LINKED_FACILITY_TYPE_CHIPS = ["SNF", "CAH", "FQHC", "RHC", "ASC"] as const;

export const LINKED_FACILITY_STATUS_CHIPS = [
  { id: "all", label: "All Status" },
  { id: "linked", label: "Linked" },
  { id: "pending", label: "Pending" },
  { id: "declined", label: "Declined" },
  { id: "cancelled", label: "Cancelled" },
  { id: "invite", label: "Invite sent" },
] as const;

export const LINKED_FACILITY_PAGE_SIZE = 25;

/** Facilities linked within this many days show "Recently linked". */
export const RECENTLY_LINKED_DAYS = 30;

const TYPE_ABBREV: Record<string, string> = {
  critical_access_hospital: "CAH",
  cah: "CAH",
  rural_health_clinic: "RHC",
  rhc: "RHC",
  federally_qualified_health_center: "FQHC",
  fqhc: "FQHC",
  ambulatory_surgical_center: "ASC",
  asc: "ASC",
  skilled_nursing_facility: "SNF",
  snf: "SNF",
  clinical_laboratory: "CL",
  clinical_lab: "CL",
  pathology_laboratory: "CL",
  hospice: "HOSP",
  multi_specialty_group: "MSG",
  multi_specialty: "MSG",
  multispecialty_group: "MSG",
  group_practice_multi: "MSG",
  single_specialty_group: "SSG",
  single_specialty: "SSG",
};

/** Normalize display/slug into TYPE_ABBREV key: strip paren notes, unify separators. */
function normalizeFacilityTypeKey(raw: string | null | undefined): string {
  return (raw || "")
    .trim()
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** NUCC taxonomy code shapes must not become type chips (e.g. 261QM1300X → 261Q). */
function isNuccTaxonomyToken(raw: string | null | undefined): boolean {
  const t = (raw || "").trim().toUpperCase();
  if (!t) return false;
  if (/^\d{3}[A-Z0-9]{1,10}X?$/.test(t)) return true;
  if (/^\d{3}[A-Z0-9]{1,3}$/.test(t)) return true;
  return false;
}

function stripNestedChildren(facility: FacilityHierarchyInput): FacilityHierarchyInput {
  return { ...facility, child_facilities: [] };
}

function digitsOnly(value: string | null | undefined): string {
  return (value || "").replace(/\D/g, "");
}

export function abbreviateFacilityType(facility: FacilityHierarchyInput): string {
  const slugKey = normalizeFacilityTypeKey(facility.facility_type_slug);
  if (slugKey && TYPE_ABBREV[slugKey]) return TYPE_ABBREV[slugKey];

  const typeKey = normalizeFacilityTypeKey(facility.facility_type);
  if (typeKey && TYPE_ABBREV[typeKey]) return TYPE_ABBREV[typeKey];

  const typeRaw = (facility.facility_type || "").trim();
  if (!typeRaw || isNuccTaxonomyToken(typeRaw)) {
    return slugKey && !isNuccTaxonomyToken(slugKey) ? slugKey.slice(0, 4).toUpperCase() : "—";
  }

  // Split on spaces and hyphens so "Multi-Specialty Group" → MSG
  const words = typeRaw
    .replace(/\([^)]*\)/g, " ")
    .split(/[\s/_-]+/)
    .map((w) => w.replace(/[^a-zA-Z0-9].*$/, "").replace(/^[^a-zA-Z0-9]+/, ""))
    .filter((w) => w.length > 0 && !isNuccTaxonomyToken(w));
  if (words.length >= 2) {
    return words
      .slice(0, 3)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("");
  }
  const single = words[0] || "";
  if (!single || isNuccTaxonomyToken(single)) return "—";
  return single.slice(0, 4).toUpperCase() || "—";
}

export function facilityCcnOrId(facility: FacilityHierarchyInput): string {
  const { ccn, npi } = networkRegistryDisplayIds(facility);
  return ccn || npi || "—";
}

export function facilityNpiValue(facility: FacilityHierarchyInput): string {
  return networkRegistryDisplayIds(facility).npi;
}

export function networkRegistryDisplayIds(
  f: FacilityHierarchyInput,
): { npi: string; ccn: string } {
  const resolved = resolveFacilityRegistryDisplayIds(f);
  const npi =
    resolved.npi ||
    bareNpiFromCcn(f.npi_number) ||
    bareNpiFromCcn(f.ccn_number);
  return { npi, ccn: resolved.ccn };
}

export const REGISTRY_ID_NA = "NA";

export function registryIdOrNa(value: string | null | undefined): string {
  const trimmed = (value || "").trim();
  return trimmed || REGISTRY_ID_NA;
}

export function formatCcnLabel(ccn: string | null | undefined): string {
  return `CCN ${registryIdOrNa(ccn)}`;
}

export function formatNpiLabel(npi: string | null | undefined): string {
  const trimmed = (npi || "").trim();
  if (trimmed && /^npi\s/i.test(trimmed)) return trimmed;
  return `NPI ${registryIdOrNa(trimmed)}`;
}

export function formatCcnNpiPair(
  ccn: string | null | undefined,
  npi: string | null | undefined,
): string {
  return `${formatCcnLabel(ccn)} · ${formatNpiLabel(npi)}`;
}

export function facilityNpiLabel(facility: FacilityHierarchyInput): string {
  return formatNpiLabel(facilityNpiValue(facility));
}

export function facilityLocation(facility: FacilityHierarchyInput): string {
  const cityState = [facility.city, facility.state].filter(Boolean).join(", ");
  if (cityState) return cityState;
  const street = [facility.address_1, facility.address_2].filter(Boolean).join(", ");
  if (street) return street;
  const full = facility.full_address?.trim();
  return full || "—";
}

export function facilityDisplayName(facility: FacilityHierarchyInput): string {
  const name = facility.facility_name?.trim() || "Facility";
  const loc = facilityLocation(facility);
  return loc !== "—" ? `${name} - ${loc}` : name;
}

export function isAcceptedLinkedStatus(raw: string): boolean {
  const value = raw.toLowerCase();
  return (
    value.includes("accept") ||
    value === "linked" ||
    value === "completed" ||
    value === "active"
  );
}

export function resolveRosterStatus(
  linkedAt: string | null | undefined,
  nowMs: number = Date.now(),
): RosterFacilityStatus {
  if (!linkedAt) return "active";
  const parsed = Date.parse(linkedAt);
  if (Number.isNaN(parsed)) return "active";
  const ageDays = (nowMs - parsed) / 86_400_000;
  return ageDays >= 0 && ageDays <= RECENTLY_LINKED_DAYS ? "recently_linked" : "active";
}

export function rosterStatusLabel(status: RosterFacilityStatus): string {
  return status === "recently_linked" ? "Recently linked" : "Active";
}

export function mapLinkedFacilityApiRows(rows: LinkedFacilityApiRow[]): LinkedFacilityRow[] {
  const mapped: LinkedFacilityRow[] = rows
    .filter((row) => isAcceptedLinkedStatus(row.status || "linked"))
    .map((row, index) => {
      const facilityStub: FacilityHierarchyInput = {
        id: row.facility_id ?? undefined,
        facility_name: row.facility_name || undefined,
        npi_number: row.npi_number?.trim() || undefined,
        ccn_number: row.ccn_number?.trim() || undefined,
        facility_type: row.facility_type || undefined,
        facility_type_slug: row.facility_type_slug || undefined,
        city: row.city || undefined,
        state: row.state || undefined,
        address_1: row.address_1 || undefined,
        child_facilities: [],
      };
      const { npi, ccn } = networkRegistryDisplayIds(facilityStub);
      const npiDigits = digitsOnly(npi);
      const ccnDigits = digitsOnly(ccn);
      const facility: FacilityHierarchyInput | null =
        row.facility_id != null ? facilityStub : null;
      const typeSource: FacilityHierarchyInput = facility ?? {
        facility_type: row.facility_type || undefined,
        facility_type_slug: row.facility_type_slug || undefined,
      };
      const typeLabel =
        row.facility_type || row.facility_type_slug
          ? abbreviateFacilityType(typeSource)
          : "—";
      const linkedAt = row.linked_at?.trim() || null;
      return {
        key:
          row.request_id != null
            ? `req-${row.request_id}`
            : row.facility_id != null
              ? `child-${row.facility_id}`
              : `row-${index}`,
        facility,
        facilityId: row.facility_id ?? null,
        name: row.facility_name?.trim() || (npi ? `NPI ${npi}` : "Facility"),
        ccnLabel: formatCcnLabel(ccn),
        npiLabel: formatNpiLabel(npi),
        npiDigits,
        ccnDigits,
        typeLabel,
        location: facilityLocation({
          city: row.city || undefined,
          state: row.state || undefined,
          address_1: row.address_1 || undefined,
        }),
        status: "linked" as const,
        rosterStatus: resolveRosterStatus(linkedAt),
        linkedAt,
        penaltyExposure: Number(row.penalty_exposure ?? 0) || 0,
      };
    });
  return sortLinkedFacilityRows(mapped, "penalty", "desc");
}

export function statusLabel(status: ChildFacilityStatus): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "declined":
      return "Declined";
    case "cancelled":
      return "Cancelled";
    case "invite":
      return "Invite sent";
    default:
      return "Linked";
  }
}

export function linkedFacilityStatusDisplay(row: LinkedFacilityRow): string {
  if (row.status === "linked") return "Linked";
  return statusLabel(row.status);
}

/** Direct children of the selected parent only — no parent row, no grandchildren. */
export function collectDirectChildren(
  networkView: FacilityNetworkViewModel,
): FacilityHierarchyInput[] {
  const selected = networkView.selected;
  const selectedId = getFacilityHierarchyId(selected);
  const nested = selected.child_facilities || [];
  if (nested.length > 0) {
    return nested.map(stripNestedChildren);
  }

  const selectedGroupId = selected.facility_group_id?.trim();
  if (selectedGroupId) {
    return networkView.members
      .filter(
        (member) =>
          member.parent_facility_group_id?.trim() === selectedGroupId &&
          getFacilityHierarchyId(member) !== selectedId,
      )
      .map(stripNestedChildren);
  }

  const anchorId = getFacilityHierarchyId(networkView.anchor);
  if (selectedId != null && selectedId === anchorId) {
    const anchorChildren = networkView.anchor.child_facilities || [];
    if (anchorChildren.length > 0) return anchorChildren.map(stripNestedChildren);
  }

  return [];
}

/** @deprecated Use collectDirectChildren — Linked Facilities is parent/child only. */
export function collectTopLevelChildren(
  networkView: FacilityNetworkViewModel,
): FacilityHierarchyInput[] {
  return collectDirectChildren(networkView);
}

export function mapOutgoingLinkRequestStatus(
  status: string,
): ChildFacilityStatus | null {
  const value = status.toLowerCase();
  if (
    value.includes("accept") ||
    value.includes("approved") ||
    value === "linked" ||
    value === "completed" ||
    value === "active"
  ) {
    return null;
  }
  if (value.includes("cancel")) {
    return "cancelled";
  }
  if (value.includes("declin") || value.includes("reject")) {
    return "declined";
  }
  if (
    value === "action_needed" ||
    value === "invite_sent" ||
    value.includes("invite")
  ) {
    return "invite";
  }
  return "pending";
}

export function mapOutgoingLinkRequestsToLinkedRows(
  requests: ChildLinkRequestItem[],
): LinkedFacilityRow[] {
  const rows: LinkedFacilityRow[] = [];
  requests.forEach((req, index) => {
    const mappedStatus = mapOutgoingLinkRequestStatus(req.status || "");
    if (!mappedStatus) return;
    const { npi, ccn } = networkRegistryDisplayIds({
      npi_number: req.npi_number || req.target_npi,
      ccn_number: req.ccn_number,
    });
    const npiDigits = digitsOnly(npi);
    const ccnDigits = digitsOnly(ccn);
    const typeSource: FacilityHierarchyInput = {
      facility_type: req.facility_type || undefined,
    };
    const typeLabel = req.facility_type
      ? abbreviateFacilityType(typeSource)
      : "—";
    rows.push({
      key: `req-${req.id ?? index}`,
      facility: null,
      facilityId: req.child_facility_id ?? null,
      name:
        req.facility_name?.trim() ||
        (npi ? `NPI ${npi}` : req.invite_email?.trim() || "Facility"),
      ccnLabel: formatCcnLabel(ccn),
      npiLabel: formatNpiLabel(npi),
      npiDigits,
      ccnDigits,
      typeLabel,
      location: facilityLocation({
        city: req.city || undefined,
        state: req.state || undefined,
      }),
      status: mappedStatus,
      rosterStatus: "active",
      linkedAt: null,
      penaltyExposure: 0,
    });
  });
  return rows;
}

/** Linked facilities + non-accepted outgoing requests (pending / declined / invite). */
export function mergeLinkedFacilityRoster(
  linkedRows: LinkedFacilityRow[],
  requestRows: LinkedFacilityRow[],
): LinkedFacilityRow[] {
  const linkedIds = new Set(
    linkedRows.map((row) => row.facilityId).filter((id): id is number => id != null),
  );
  const linkedNpis = new Set(
    linkedRows
      .map((row) => (row.npiDigits.length >= 10 ? row.npiDigits.slice(-10) : ""))
      .filter(Boolean),
  );
  const extras = requestRows.filter((row) => {
    if (row.facilityId != null && linkedIds.has(row.facilityId)) return false;
    const npiKey = row.npiDigits.length >= 10 ? row.npiDigits.slice(-10) : "";
    if (npiKey && linkedNpis.has(npiKey)) return false;
    return true;
  });
  return sortLinkedFacilityRows([...linkedRows, ...extras], "status", "asc");
}

export function buildLinkedFacilityRows(
  children: FacilityHierarchyInput[],
  requests: ChildLinkRequestItem[] = [],
): LinkedFacilityRow[] {
  const linkedRows = children.map((facility, index) => {
    const facilityId = getFacilityHierarchyId(facility);
    const { ccn, npi } = networkRegistryDisplayIds(facility);
    return {
      key: facilityId != null ? `child-${facilityId}` : `child-idx-${index}`,
      facility,
      facilityId,
      name: facility.facility_name?.trim() || "Facility",
      ccnLabel: formatCcnLabel(ccn),
      npiLabel: facilityNpiLabel(facility),
      npiDigits: digitsOnly(npi),
      ccnDigits: digitsOnly(ccn),
      typeLabel: abbreviateFacilityType(facility),
      location: facilityLocation(facility),
      status: "linked" as const,
      rosterStatus: "active" as const,
      linkedAt: null,
      penaltyExposure: 0,
    };
  });

  return mergeLinkedFacilityRoster(
    linkedRows,
    mapOutgoingLinkRequestsToLinkedRows(requests),
  );
}

export function countNetworkFacilities(roots: FacilityHierarchyInput[]): number {
  return roots.length;
}

/**
 * Search: name case-insensitive partial; CCN exact; NPI exact (AB#10988).
 * Type / status filter chips are mutually exclusive within each group; "all" clears.
 */
export function filterLinkedFacilityRows(
  rows: LinkedFacilityRow[],
  query: string,
  typeFilter: string,
  statusFilter: string = "all",
): LinkedFacilityRow[] {
  const q = query.trim();
  const qLower = q.toLowerCase();
  const qDigits = digitsOnly(q);
  return rows.filter((row) => {
    if (typeFilter !== "all" && row.typeLabel !== typeFilter) return false;
    if (statusFilter !== "all" && row.status !== statusFilter) return false;
    if (!q) return true;
    if (row.name.toLowerCase().includes(qLower)) return true;
    if (row.location.toLowerCase().includes(qLower)) return true;
    if (qDigits && row.ccnDigits && row.ccnDigits === qDigits) return true;
    if (qDigits && row.npiDigits && row.npiDigits === qDigits) return true;
    return false;
  });
}

/** @deprecated Use filterLinkedFacilityRows */
export function filterChildRows(
  rows: LinkedFacilityRow[],
  query: string,
  typeFilter: string,
  statusFilter: string,
): LinkedFacilityRow[] {
  const mappedStatus =
    statusFilter === "rejected" || statusFilter === "active" || statusFilter === "linked"
      ? statusFilter === "rejected"
        ? "declined"
        : "linked"
      : statusFilter;
  return filterLinkedFacilityRows(rows, query, typeFilter, mappedStatus);
}

export function listTypeOptions(children: FacilityHierarchyInput[]): string[] {
  const types = new Set<string>();
  children.forEach((facility) => {
    types.add(abbreviateFacilityType(facility));
  });
  return Array.from(types).filter((t) => t !== "—").sort();
}

export function enrichLinkedFacilityRows(
  rows: LinkedFacilityRow[],
  members: FacilityHierarchyInput[] = [],
): LinkedFacilityRow[] {
  const byId = new Map<number, FacilityHierarchyInput>();
  const byNpi = new Map<string, FacilityHierarchyInput>();
  members.forEach((member) => {
    const id = getFacilityHierarchyId(member);
    if (id != null) byId.set(id, member);
    const digits = facilityNpiValue(member).replace(/\D/g, "");
    if (digits.length >= 10) byNpi.set(digits.slice(-10), member);
  });
  return rows.map((row) => {
    const needsName = isRegistryPlaceholderName(row.name, row.npiDigits);
    if (
      row.typeLabel !== "—" &&
      row.location !== "—" &&
      row.ccnDigits &&
      row.npiDigits &&
      !needsName
    ) {
      return row;
    }
    const npiDigits = row.npiDigits;
    const npiKey = npiDigits.length >= 10 ? npiDigits.slice(-10) : "";
    const facility =
      (row.facilityId != null ? byId.get(row.facilityId) : undefined) ||
      (npiKey ? byNpi.get(npiKey) : undefined);
    if (!facility) return row;
    const { ccn, npi } = networkRegistryDisplayIds(facility);
    const nextCcn = row.ccnDigits || digitsOnly(ccn);
    const nextNpi = row.npiDigits || digitsOnly(npi);
    const memberName = facility.facility_name?.trim() || "";
    return {
      ...row,
      facility: row.facility ?? stripNestedChildren(facility),
      name:
        needsName && memberName && !isRegistryPlaceholderName(memberName, nextNpi)
          ? memberName
          : row.name,
      typeLabel: row.typeLabel === "—" ? abbreviateFacilityType(facility) : row.typeLabel,
      location: row.location === "—" ? facilityLocation(facility) : row.location,
      ccnLabel: nextCcn ? formatCcnLabel(nextCcn) : formatCcnLabel(ccn),
      npiLabel: nextNpi ? formatNpiLabel(nextNpi) : formatNpiLabel(npi),
      ccnDigits: nextCcn,
      npiDigits: nextNpi,
    };
  });
}

export type LinkedFacilityNpiHit = {
  name?: string;
  facilityType?: string;
  city: string;
  state: string;
  ccn?: string;
};

/** True when list name is empty or just the NPI/CCN placeholder. */
export function isRegistryPlaceholderName(
  name: string | null | undefined,
  npiDigits: string = "",
): boolean {
  const trimmed = (name || "").trim();
  if (!trimmed) return true;
  const npi = npiDigits.replace(/\D/g, "");
  const npiKey = npi.length >= 10 ? npi.slice(-10) : npi;
  if (npiKey) {
    if (trimmed === npiKey || trimmed === `NPI ${npiKey}` || trimmed === `CCN ${npiKey}`) {
      return true;
    }
    if (trimmed === `CCN / NPI ${npiKey}`) return true;
  }
  if (/^NPI\s+\d+$/i.test(trimmed)) return true;
  if (/^CCN\s+\d+$/i.test(trimmed)) return true;
  if (/^CCN\s*\/\s*NPI\s+\d+$/i.test(trimmed)) return true;
  if (/^Facility \(CCN\s+/i.test(trimmed)) return true;
  return false;
}

export function applyNpiSearchHitsToRows(
  rows: LinkedFacilityRow[],
  hitsByNpi: Record<string, LinkedFacilityNpiHit>,
): LinkedFacilityRow[] {
  return rows.map((row) => {
    const npi = row.npiDigits.slice(-10);
    const hit = npi.length === 10 ? hitsByNpi[npi] : undefined;
    if (!hit) return row;
    const typeLabel =
      row.typeLabel !== "—"
        ? row.typeLabel
        : hit.facilityType
          ? abbreviateFacilityType({
              facility_type: hit.facilityType,
              facility_type_slug: hit.facilityType.toLowerCase().replace(/\s+/g, "_"),
            })
          : row.typeLabel;
    const location =
      row.location !== "—"
        ? row.location
        : facilityLocation({ city: hit.city, state: hit.state });
    const ccn = (hit.ccn || "").trim();
    const ccnDigits = row.ccnDigits || digitsOnly(ccn);
    const hitName = (hit.name || "").trim();
    const name =
      hitName && isRegistryPlaceholderName(row.name, row.npiDigits)
        ? hitName
        : row.name;
    return {
      ...row,
      name,
      typeLabel,
      location,
      ccnDigits,
      ccnLabel: ccnDigits ? formatCcnLabel(ccnDigits) : row.ccnLabel,
    };
  });
}

export function listTypeOptionsFromRows(
  rows: LinkedFacilityRow[],
  selectedType?: string,
): string[] {
  const types = new Set<string>();
  rows.forEach((row) => {
    if (row.typeLabel && row.typeLabel !== "—") types.add(row.typeLabel);
  });
  if (selectedType && selectedType !== "all") types.add(selectedType);
  const fixedOrder = LINKED_FACILITY_TYPE_CHIPS.filter((type) => types.has(type));
  const extras = Array.from(types)
    .filter((type) => !(LINKED_FACILITY_TYPE_CHIPS as readonly string[]).includes(type))
    .sort((a, b) => a.localeCompare(b));
  return [...fixedOrder, ...extras];
}

export function compareLinkedFacilityRows(
  a: LinkedFacilityRow,
  b: LinkedFacilityRow,
  sortKey: LinkedFacilitySortKey,
  sortDir: "asc" | "desc",
): number {
  const dir = sortDir === "asc" ? 1 : -1;
  let primary = 0;
  switch (sortKey) {
    case "facility":
      primary = a.name.localeCompare(b.name);
      break;
    case "type":
      primary = a.typeLabel.localeCompare(b.typeLabel);
      break;
    case "location":
      primary = a.location.localeCompare(b.location);
      break;
    case "status":
      primary = linkedFacilityStatusDisplay(a).localeCompare(
        linkedFacilityStatusDisplay(b),
      );
      break;
    case "penalty":
    default:
      primary = a.penaltyExposure - b.penaltyExposure;
      break;
  }
  if (primary !== 0) return primary * dir;
  // Default secondary: penalty DESC then name ASC when primary was penalty;
  // otherwise name ASC as stable tie-breaker.
  if (sortKey === "penalty") {
    return a.name.localeCompare(b.name);
  }
  const byPenalty = b.penaltyExposure - a.penaltyExposure;
  if (byPenalty !== 0) return byPenalty;
  return a.name.localeCompare(b.name);
}

export function sortLinkedFacilityRows(
  rows: LinkedFacilityRow[],
  sortKey: LinkedFacilitySortKey = "penalty",
  sortDir: "asc" | "desc" = "desc",
): LinkedFacilityRow[] {
  return [...rows].sort((a, b) => compareLinkedFacilityRows(a, b, sortKey, sortDir));
}

export function paginateLinkedFacilityRows(
  rows: LinkedFacilityRow[],
  page: number,
  pageSize: number = LINKED_FACILITY_PAGE_SIZE,
  showAll: boolean = false,
): { pageRows: LinkedFacilityRow[]; totalPages: number; page: number } {
  if (showAll || pageSize <= 0 || rows.length <= pageSize) {
    return { pageRows: rows, totalPages: 1, page: 1 };
  }
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    pageRows: rows.slice(start, start + pageSize),
    totalPages,
    page: safePage,
  };
}

export function linkedFacilitiesSubtitle(count: number): string {
  return `${count} facilit${count === 1 ? "y" : "ies"} directly linked to your network`;
}

/** Build dashboard facility stub from linked roster — child need not be in My Facilities. */
export function linkedFacilityRowToDashboardFacility(
  row: LinkedFacilityRow,
): MyFacilityListRow | null {
  if (row.facilityId == null) return null;
  const f = row.facility;
  const city =
    (f && typeof f.city === "string" && f.city) ||
    row.location.split(",")[0]?.trim() ||
    "";
  const state =
    (f && typeof f.state === "string" && f.state) ||
    row.location.split(",")[1]?.trim() ||
    "";
  return {
    facility_id: row.facilityId,
    id: row.facilityId,
    facility_name: row.name,
    city,
    state,
    full_address: [city, state].filter(Boolean).join(", "),
    facility_type:
      (f && typeof f.facility_type === "string" && f.facility_type) || row.typeLabel || "",
    facility_type_slug:
      (f && typeof f.facility_type_slug === "string" && f.facility_type_slug) || undefined,
    org_id: 0,
    org_name: "",
    ccn_number: row.ccnDigits || null,
    npi_number: row.npiDigits || null,
    address_1: f && typeof f.address_1 === "string" ? f.address_1 : null,
    is_child_facility: true,
  };
}
